<script setup lang="ts">
import { computed } from "vue";
import InputLabel from "@/components/ui/InputLabel.vue";

type Item = Record<string, unknown>;

type Props = {
  indexKey: string;
  labelKey: string;
  options: Item[];
  modelValue: unknown[];
  label?: string;
  labelDisabled?: boolean;
};

type Emits = {
  (e: "update:modelValue", value: unknown[]): void;
};

const props = withDefaults(defineProps<Props>(), {
  label: "",
  labelDisabled: false,
});

const emit = defineEmits<Emits>();

const selected = computed(() => {
  return props.options.filter((item) => !filterOutSelected(item));
});

const notSelected = computed(() => {
  return props.options.filter((item) => filterOutSelected(item));
});

function filterOutSelected(check: Item): boolean {
  return !props.modelValue.some((value) => check[props.indexKey] === value);
}

function getLabel(value: Item): string {
  return String(value[props.labelKey] ?? "");
}

function removeItem(item: Item): void {
  const newValues = props.modelValue.filter(
    (value) => value !== item[props.indexKey],
  );
  emit("update:modelValue", newValues);
}

function addItem(item: Item): void {
  const newValues = [...props.modelValue, item[props.indexKey]];
  emit("update:modelValue", newValues);
}
</script>

<template>
  <div>
    <input-label :text="label" :disabled="labelDisabled" />

    <div class="mb-4 flex flex-wrap items-center gap-x-2 align-middle">
      <div
        v-for="(item, index) in selected"
        :key="index"
        class="mb-2 flex cursor-pointer flex-nowrap items-center gap-x-2 rounded-2xl bg-kekkle-blue p-1 px-2 align-middle text-slate-900"
        @click="removeItem(item)"
      >
        <div
          class="flex h-3 w-3 items-center justify-center rounded-full bg-kekkle-blue-dark p-2 text-center align-middle"
        >
          <p class="text-xs">X</p>
        </div>
        <p>{{ getLabel(item) }}</p>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-x-2 align-middle">
      <div
        v-for="(item, index) in notSelected"
        :key="index"
        class="mb-2 flex cursor-pointer flex-nowrap items-center gap-x-2 rounded-2xl bg-kekkle-yellow p-1 px-2 align-middle text-slate-900"
        @click="addItem(item)"
      >
        <div
          class="flex h-3 w-3 items-center justify-center rounded-full bg-kekkle-yellow-dark p-2 text-center align-middle"
        >
          <p class="text-xs">+</p>
        </div>
        <p>{{ getLabel(item) }}</p>
      </div>
    </div>
  </div>
</template>
